import { largestPiece, noteConfidenceBand, NOTE_ASKS_FOR_LABELS, NOTE_CODE_LABELS, type CanonicalLoad, type LoadNoteFacts } from '@numeo/types-core'

/**
 * What a model read out of the posting's note (ADR-049).
 *
 * The note above this says the same thing in a broker's shorthand. This says it in fields, which is what makes
 * it worth showing twice: `LG/PJ` is a lift gate and a pallet jack to a dispatcher who has read a thousand of
 * these, and nothing at all to one who has not.
 *
 * ABSENT IS NOT EMPTY. A load with no reading renders nothing — no heading, no empty state. Most loads will be
 * that way for as long as the extractor is behind, and a panel saying "nothing found" would read as "the note
 * asks for nothing", which is a different and wrong claim.
 */

const Chip = ({ children, tone = 'need' }: { children: React.ReactNode; tone?: 'need' | 'not' }) => (
    <span
        className={
            tone === 'need'
                ? 'rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent'
                : 'rounded-full bg-background px-2 py-0.5 text-[11px] font-medium text-foreground-muted line-through'
        }
    >
        {children}
    </span>
)

const Line = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="shrink-0 text-xs font-medium text-foreground-muted">{label}</span>
        <span className="min-w-0 text-xs font-semibold text-foreground">{children}</span>
    </div>
)

/** Inches as a dispatcher says them: whole feet when it divides, inches otherwise. */
const inches = (value: number): string => (value % 12 === 0 ? `${value / 12} ft` : `${value} in`)

const piecesText = (facts: LoadNoteFacts): string | null => {
    const pieces = facts.cargoPieces ?? []
    if (pieces.length === 0) return null
    return pieces
        .map((p) => {
            const size = `${p.lengthIn}×${p.widthIn}×${p.heightIn} in`
            const count = p.count ? `${p.count} × ` : ''
            const weight = p.weightLbs ? `, ${p.weightLbs.toLocaleString()} lbs` : ''
            return `${count}${size}${weight}`
        })
        .join(' · ')
}

const truckText = (facts: LoadNoteFacts): string | null => {
    const { lengthIn, widthIn, heightIn } = facts.minTruckCargo ?? {}
    const parts = [lengthIn && `${inches(lengthIn)} long`, widthIn && `${inches(widthIn)} wide`, heightIn && `${inches(heightIn)} tall`].filter(Boolean)
    return parts.length > 0 ? `at least ${parts.join(', ')}` : null
}

const temperatureText = (facts: LoadNoteFacts): string | null => {
    const { minF, maxF } = facts.temperatureF ?? {}
    if (minF !== undefined && maxF !== undefined) return minF === maxF ? `${minF}°F` : `${minF}–${maxF}°F`
    if (minF !== undefined) return `${minF}°F or above`
    if (maxF !== undefined) return `${maxF}°F or below`
    return null
}

const SERVICE_LABELS: Record<NonNullable<LoadNoteFacts['serviceLevel']>, string> = {
    READY_NOW: 'Ready now',
    ASAP: 'ASAP',
    DIRECT: 'Direct',
}

const CONTACT_LABELS: Record<NonNullable<LoadNoteFacts['contact']>, string> = {
    EMAIL_ONLY: 'Email only',
    NO_CALLS: 'No calls',
    CALL_OK: 'Calls welcome',
}

export function BoxTruckNoteFacts({ load }: { load: CanonicalLoad }) {
    const facts = load.details?.noteFacts
    if (!facts) return null

    const needs = facts.needs ?? []
    const forbids = facts.forbids ?? []
    const truck = truckText(facts)
    const pieces = piecesText(facts)
    const temperature = temperatureText(facts)
    const largest = largestPiece(facts)
    const asksFor = facts.asksFor ?? []
    const contact = [facts.contact && CONTACT_LABELS[facts.contact], facts.appointment === 'STRICT' && 'Hard appointment', facts.exclusiveUse && 'Whole truck']
        .filter(Boolean)
        .join(' · ')

    return (
        <div className="order-4 mt-2 rounded-lg border border-border bg-background px-3 py-2.5 min-[900px]:order-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">What the note asks for</div>

            <div className="mt-2 flex flex-col gap-1.5">
                {needs.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {needs.map((code) => (
                            <Chip key={code}>{NOTE_CODE_LABELS[code]}</Chip>
                        ))}
                        {/* What the note says is NOT needed, struck through: it is a real statement and a
                            dispatcher who misses it refuses a load the truck could have taken. */}
                        {forbids.map((code) => (
                            <Chip key={code} tone="not">
                                {NOTE_CODE_LABELS[code]}
                            </Chip>
                        ))}
                    </div>
                )}
                {needs.length === 0 && forbids.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {forbids.map((code) => (
                            <Chip key={code} tone="not">
                                {NOTE_CODE_LABELS[code]}
                            </Chip>
                        ))}
                    </div>
                )}

                {truck && <Line label="Truck">{truck}</Line>}
                {pieces && (
                    <Line label="Freight">
                        {pieces}
                        {largest && (
                            <span className="ml-1 font-medium text-foreground-muted">
                                (largest {largest.widthIn}×{largest.heightIn} in must clear the door)
                            </span>
                        )}
                    </Line>
                )}
                {temperature && <Line label="Temperature">{temperature}</Line>}
                {facts.serviceLevel && <Line label="Service">{SERVICE_LABELS[facts.serviceLevel]}</Line>}
                {contact && <Line label="Contact">{contact}</Line>}
                {asksFor.length > 0 && <Line label="Reply with">{asksFor.map((ask) => NOTE_ASKS_FOR_LABELS[ask]).join(', ')}</Line>}
                {facts.referenceNumber && <Line label="Reference">{facts.referenceNumber}</Line>}
                {facts.otherText && <Line label="Also">{facts.otherText}</Line>}
            </div>

            {/* A reading is an inference, and a dispatcher deciding on it should be able to see how sure it was. */}
            <div className="mt-2 border-t border-border pt-1.5 text-[10px] text-foreground-muted">
                Read from the note{noteConfidenceBand(facts.confidence) === 'UNSURE' ? ' — low confidence, check the note itself' : ''}
            </div>
        </div>
    )
}
